import { format } from 'date-fns';
import { DateTime } from 'luxon';

import { Rules, RehabRuleChoiceListFiles } from '@Types';
import { FIELDS_TYPES, RECORD_TYPE } from '@Utils/choiceListMapping';
import {
	getValueTypeForSpecificOperator,
	initMultiSelectFieldForEdit,
} from '@Utils/choiceListFilesUtils';

export const getTimeStampFromDateTime = (date: string) => {
	//adding .replace(/-/g, "/") to solve the issue in safari
	return new Date(date.replace(/-/g, '/'));
};

const PACP_OR_WSA_FIELDS = new Set(['pacp_code', 'wsa_code']);

function isPacpOrWsaField(field: string | undefined): boolean {
	if (!field) {
		return false;
	}
	return PACP_OR_WSA_FIELDS.has(field.toLowerCase());
}

function usesChoiceListForMultiSelect(recordType: string | undefined): boolean {
	return (
		recordType === RECORD_TYPE.INSPECTION ||
		recordType === RECORD_TYPE.DETERIORATION_MODEL
	);
}

function multiSelectValue1ForInspectionLike(
	rule: Rules,
	choiceListValues: ReturnType<typeof initMultiSelectFieldForEdit>,
) {
	if (isPacpOrWsaField(rule.field)) {
		return rule.value1;
	}
	return rule.value1 && choiceListValues;
}

function multiSelectValue1ForOtherRecordTypes(rule: Rules) {
	if (isPacpOrWsaField(rule.field)) {
		return rule.value1;
	}
	if (!rule.value1) {
		return rule.value1;
	}
	return (rule.value1 as string[]).map((value: string) => ({
		label: value,
		value: value,
	}));
}

function buildMultiSelectInitialValues(
	rule: Rules,
	recordTypeChoiceListFiles: RehabRuleChoiceListFiles,
	operator: string | undefined,
	config: string | undefined,
) {
	const shared = {
		id: rule.id,
		recordType: rule.recordType,
		config,
		field: rule.field,
		operator,
		value2: rule.value2,
		unitLength: rule.unitLength,
		values: rule.values,
		ruleCondition: rule.ruleCondition,
		unit: rule.unit,
		filters: rule.filters,
		compare: rule.compare,
	};

	if (usesChoiceListForMultiSelect(rule.recordType)) {
		const choiceListValues = initMultiSelectFieldForEdit(
			recordTypeChoiceListFiles,
			rule,
		);
		return {
			...shared,
			value1: multiSelectValue1ForInspectionLike(rule, choiceListValues),
		};
	}

	return {
		...shared,
		value1: multiSelectValue1ForOtherRecordTypes(rule),
	};
}

function luxonUtcToWallClock(iso: string): DateTime {
	const luxonDt = DateTime.fromISO(iso);
	return luxonDt.minus({ minutes: luxonDt.offset });
}

function buildDateOneFieldInitialValues(
	rule: Rules,
	operator: string | undefined,
	config: string | undefined,
) {
	// adjusting UTC datetime to local time while keeping the same
	// date. This is needed because date picker can only work with
	// local time.
	const value1Arr = rule.value1 as Array<string> | undefined;
	const hasValue =
		value1Arr && Array.isArray(value1Arr) && value1Arr.length > 0;
	let value1: Date | string = '';
	if (hasValue && value1Arr) {
		const luxonDt = luxonUtcToWallClock(String(value1Arr[0]));
		value1 = getTimeStampFromDateTime(
			format(luxonDt.toJSDate(), 'yyyy-MM-dd HH:mm:ss'),
		);
	}

	return {
		id: rule.id,
		recordType: rule.recordType,
		config,
		field: rule.field,
		operator,
		value1,
		value2: rule.value2,
		unitLength: rule.unitLength,
		ruleCondition: rule.ruleCondition,
		unit: rule.unit,
		filters: rule.filters,
		compare: rule.compare,
	};
}

function buildDateTwoFieldsInitialValues(
	rule: Rules,
	operator: string | undefined,
	config: string | undefined,
) {
	const value1Arr = rule.value1 as Array<string> | undefined;
	const hasValue1 =
		value1Arr && Array.isArray(value1Arr) && value1Arr.length > 0;
	let value1: Date | unknown = rule.value1;
	if (hasValue1 && value1Arr) {
		const luxonDt = luxonUtcToWallClock(String(value1Arr[0]));
		value1 = getTimeStampFromDateTime(
			format(luxonDt.toJSDate(), 'yyyy-MM-dd HH:mm:ss'),
		);
	}

	const luxonDt2 = DateTime.fromISO(rule.value2 as string);
	const luxonDt2Adjusted = luxonDt2.minus({ minutes: luxonDt2.offset });
	const value2 = rule.value2
		? getTimeStampFromDateTime(
				format(luxonDt2Adjusted.toJSDate(), 'yyyy-MM-dd HH:mm:ss'),
			)
		: rule.value2;

	return {
		id: rule.id,
		recordType: rule.recordType,
		config,
		field: rule.field,
		operator,
		value1,
		unitLength: rule.unitLength,
		value2,
		values: rule.values,
		ruleCondition: rule.ruleCondition,
		unit: rule.unit,
		filters: rule.filters,
		compare: rule.compare,
	};
}

function firstArrayElementOrScalar<T>(value: unknown): T | string | undefined {
	if (value && Array.isArray(value)) {
		return value.length > 0 ? (value as T[])[0] : '';
	}
	return value as T | undefined;
}

function buildDefaultFieldInitialValues(
	rule: Rules,
	operator: string | undefined,
	config: string | undefined,
) {
	return {
		id: rule.id,
		recordType: rule.recordType,
		config,
		field: rule.field,
		operator,
		value1: firstArrayElementOrScalar(rule.value1),
		value2: firstArrayElementOrScalar(rule.value2),
		unitLength: rule.unitLength,
		values: rule.values,
		ruleCondition: rule.ruleCondition,
		unit: rule.unit,
		filters: rule.filters,
		compare: rule.compare,
	};
}

function normalizeOperator(operator: string | undefined) {
	if (!operator) {
		return operator;
	}
	return operator.replace('DOSE_NOT_EQUALS', 'DOES_NOT_EQUAL');
}

function resolveValueType(
	rule: Rules,
	operator: string | undefined,
	recordTypeChoiceListFiles: RehabRuleChoiceListFiles,
): string {
	if (!rule.recordType || !rule.field || !operator) {
		return '';
	}
	return getValueTypeForSpecificOperator(
		recordTypeChoiceListFiles,
		rule.recordType,
		rule.field,
		operator,
		rule.config,
	);
}

/**
 * Maps a persisted rule into form initial values for the query / rule drawer.
 * Split into helpers to keep cognitive complexity low (SonarQube).
 */
export function getInitialValuesForEdit(
	rule: Rules,
	recordTypeChoiceListFiles: RehabRuleChoiceListFiles,
): Rules {
	const operator = normalizeOperator(rule.operator);
	const valueType = resolveValueType(
		rule,
		operator,
		recordTypeChoiceListFiles,
	);
	const config = 'config' in rule ? rule.config : '';

	switch (valueType) {
		case FIELDS_TYPES.MULTI_SELECT_ONE_FIELD:
			return buildMultiSelectInitialValues(
				rule,
				recordTypeChoiceListFiles,
				operator,
				config,
			) as Rules;
		case FIELDS_TYPES.DATE_ONE_FIELD:
			return buildDateOneFieldInitialValues(
				rule,
				operator,
				config,
			) as Rules;
		case FIELDS_TYPES.DATE_TWO_FIELDS:
			return buildDateTwoFieldsInitialValues(
				rule,
				operator,
				config,
			) as Rules;
		case FIELDS_TYPES.BOOLEAN:
		case FIELDS_TYPES.TEXT_ONE_FIELD:
		case FIELDS_TYPES.PERCENTAGE_TEXT:
		case FIELDS_TYPES.NUMERICAL_ONE_FIELD:
		case FIELDS_TYPES.NUMERICAL_UNRESTRICTED_ONE_FIELD:
		case FIELDS_TYPES.CLOCK_ONE_FIELD:
		case FIELDS_TYPES.CLOCK_EXTENT_ONE_FIELD:
		case FIELDS_TYPES.PERCENTAGE_TWO_FIELDS:
		case FIELDS_TYPES.NUMERICAL_TWO_FIELDS:
		case FIELDS_TYPES.NUMERICAL_UNRESTRICTED_TWO_FIELDS:
		case FIELDS_TYPES.CLOCK_TWO_FIELDS:
		case FIELDS_TYPES.CLOCK_EXTENT_TWO_FIELDS:
		case FIELDS_TYPES.DAYS_SINCE:
		case FIELDS_TYPES.YEARS_SINCE:
		default:
			return buildDefaultFieldInitialValues(
				rule,
				operator,
				config,
			) as Rules;
	}
}
